// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-4: the detailed team invite (Team page only, per Patrick 2026-08-18).
 * Captures the FULL member setup at invite time — account info, working hours,
 * manager, customer coverage — pre-staged by email and auto-applied when the
 * invite is accepted. Supervisor invites agents; manager invites any tier.
 *
 * D-SUPPORT-10 (Patrick 2026-08-21) — three complaints, all of them fair:
 *
 *  1. "I hit invite and saw no feedback, the form cleaned up, and I don't know
 *     if he got it." The confirmation was 12px muted text BELOW the button on a
 *     ~3,700px-tall form, and it evaporated on the next re-render. It is now a
 *     banner at the TOP of the form that stays until dismissed, and the Team
 *     page lists pending invitations with their mail-delivery state so the
 *     answer survives the page reload.
 *  2. Manager was a type-ahead over support staff only — it now lists every
 *     active Owl employee.
 *  3. Typing someone who already has an account produced a SECOND invitation
 *     for them. It now matches existing staff as you type and switches to
 *     "add to the team", which grants the role and emails a heads-up rather
 *     than minting a redundant credential path.
 */
import { useEffect, useMemo, useState } from 'react'
import { UserPlus, CheckCircle2, X, AlertTriangle } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import SearchableSelect from '../../components/common/SearchableSelect'

type Outcome =
  | { kind: 'invited'; email: string; authMode: 'sso' | 'password'; link: string }
  | { kind: 'added'; email: string; name: string; role: string }

const TIER_LABEL: Record<string, string> = {
  support: 'Agent',
  support_supervisor: 'Supervisor',
  support_manager: 'Manager',
}

export default function SupportInviteBar({ onInvited }: { onInvited?: () => void }) {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('support.tickets.manage')
  const isManager = hasPermission('support.settings.manage')
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('support')
  const [title, setTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [ws, setWs] = useState('08:00')
  const [we, setWe] = useState('17:00')
  const [wd, setWd] = useState('Mon-Fri')
  const [mgr, setMgr] = useState('')
  const [cov, setCov] = useState<Set<string>>(new Set())
  const [people, setPeople] = useState<api.SupportPlatformUser[]>([])
  const [peopleError, setPeopleError] = useState<string | null>(null)
  const [customers, setCustomers] = useState<api.SupportCustomer[]>([])
  const [picked, setPicked] = useState<api.SupportPlatformUser | null>(null)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    // A failed load used to be swallowed, which left the Manager box empty and looking
    // exactly like a free-text field. Say so instead.
    api.listSupportPlatformUsers()
      .then(r => { setPeople(r.users); setPeopleError(null) })
      .catch(e => setPeopleError(friendlyError(e)))
    api.listSupportCustomers().then(r => setCustomers(r.customers)).catch(() => {})
  }, [open])

  const reset = () => {
    setEmail(''); setName(''); setTitle(''); setPhone(''); setCov(new Set()); setPicked(null)
  }

  // Type-ahead over existing Owl staff, matched on email OR name — Patrick types either.
  const query = (email || name).trim().toLowerCase()
  const suggestions = useMemo(() => {
    if (picked || query.length < 2) return []
    return people
      .filter(p => p.email.toLowerCase().includes(query) || (p.display_name ?? '').toLowerCase().includes(query))
      .slice(0, 6)
  }, [people, query, picked])

  if (!canManage) return null

  const alreadyOnTeam = picked?.support_role != null
  const submitLabel = busy
    ? (picked ? 'Adding…' : 'Inviting…')
    : (picked ? 'Add to support team' : 'Send invite')

  const submit = () => {
    setBusy(true); setOutcome(null); setError(null)
    const shared = {
      title: title.trim() || undefined,
      phone: phone.trim() || undefined,
      work_start: ws || undefined,
      work_end: we || undefined,
      work_days: wd || undefined,
      reports_to_account_id: mgr || undefined,
      pharmacy_ids: [...cov],
    }
    const call = picked
      ? api.addExistingSupportMember({ account_id: picked.id, role, ...shared })
          .then(r => { setOutcome({ kind: 'added', email: r.email, name: r.display_name, role: r.role }) })
      : api.inviteSupportTeammate({ email: email.trim(), role: role as any, display_name: name.trim() || undefined, ...shared })
          .then(r => { setOutcome({ kind: 'invited', email: email.trim(), authMode: r.auth_mode ?? 'password', link: r.bootstrap_link }) })

    call
      .then(() => { reset(); onInvited?.() })
      .catch(e => setError(friendlyError(e)))
      .finally(() => setBusy(false))
  }

  return (
    <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', cursor: 'pointer', padding: 0, fontSize: 13.5, fontWeight: 700, color: 'var(--color-text)' }}>
        <UserPlus size={16} /> Invite teammate {open ? '▾' : '▸'}
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          {/* Outcome sits ABOVE the form, not below the button at the bottom of a very tall
              panel, and it persists until dismissed. This is the whole point of the fix. */}
          {outcome && (
            <div role="status" style={banner('#ecfdf5', '#a7f3d0', '#065f46')}>
              <CheckCircle2 size={17} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1, lineHeight: 1.5 }}>
                {outcome.kind === 'added' ? (
                  <>
                    <b>{outcome.name}</b> is now on the support team as <b>{TIER_LABEL[outcome.role] ?? outcome.role}</b>.
                    {' '}They already had an account, so there was no invitation to accept — we emailed <b>{outcome.email}</b> to let them know.
                    {' '}The role applies at their next sign-in.
                  </>
                ) : (
                  <>
                    <b>Invitation created for {outcome.email}</b> and the email has been queued.
                    {' '}{outcome.authMode === 'sso'
                      ? <>They sign in with their <b>work account</b> — no password to set. Portal login: <b>{outcome.link}</b></>
                      : <>Password-setup link, if you want to pass it on directly: <b>{outcome.link}</b></>}
                    <div style={{ marginTop: 4, fontSize: 12.5, opacity: .85 }}>
                      Queued is not the same as delivered — the <b>Pending invitations</b> list below shows whether it actually sent.
                    </div>
                  </>
                )}
              </div>
              <button onClick={() => setOutcome(null)} aria-label="Dismiss" style={dismiss}><X size={15} /></button>
            </div>
          )}
          {error && (
            <div role="alert" style={banner('#fef2f2', '#fecaca', '#991b1b')}>
              <AlertTriangle size={17} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1, lineHeight: 1.5 }}>{error}</div>
              <button onClick={() => setError(null)} aria-label="Dismiss" style={dismiss}><X size={15} /></button>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <label style={{ ...lab, position: 'relative' }}>Email *
              <input type="email" value={email} onChange={e => { setEmail(e.target.value); setPicked(null) }}
                placeholder="ahmed@owlconsultations.com" style={inp} autoComplete="off" />
              {suggestions.length > 0 && (
                <div style={sugg}>
                  <div style={{ padding: '5px 9px', fontSize: 11, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', color: 'var(--color-text-mute)' }}>
                    Already has an Owl account
                  </div>
                  {suggestions.map(p => (
                    <button key={p.id} type="button"
                      onClick={() => { setPicked(p); setEmail(p.email); setName(p.display_name ?? '') }}
                      style={suggRow}>
                      <span style={{ fontWeight: 600 }}>{p.display_name ?? p.email}</span>
                      <span style={{ color: 'var(--color-text-mute)', fontSize: 12 }}>{p.email}</span>
                      {p.support_role && (
                        <span style={{ fontSize: 11, color: '#92400e', background: '#fef3c7', borderRadius: 999, padding: '1px 7px' }}>
                          already {TIER_LABEL[p.support_role] ?? p.support_role}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </label>
            <label style={lab}>Full name
              <input value={name} onChange={e => { setName(e.target.value); setPicked(null) }}
                placeholder="Ahmed …" style={inp} disabled={!!picked} />
            </label>
            <label style={lab}>Role
              <SearchableSelect value={role} onChange={setRole} ariaLabel="Invite role"
                options={[{ value: 'support', label: 'Agent' },
                          ...(isManager ? [{ value: 'support_supervisor', label: 'Supervisor' }, { value: 'support_manager', label: 'Manager' }] : [])]}
                style={{ fontSize: 13 }} />
            </label>
            <label style={lab}>Title
              <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Support Agent I" style={inp} />
            </label>
            <label style={lab}>Phone
              <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 …" style={inp} />
            </label>
            <label style={lab}>Manager
              {/* Every active Owl employee, not just support staff — reporting lines here do not
                  follow the support tier ladder (Patrick 2026-08-21). */}
              <SearchableSelect value={mgr} onChange={setMgr} ariaLabel="Reports to" placeholder="Reports to…"
                options={[{ value: '', label: '— none —' },
                          ...people.map(p => ({ value: p.id, label: p.display_name ?? p.email }))]}
                style={{ fontSize: 13 }} />
              {peopleError
                ? <span style={{ fontSize: 11.5, color: '#b91c1c', fontWeight: 400 }}>Could not load Owl staff — {peopleError}</span>
                : people.length === 0
                  ? <span style={{ fontSize: 11.5, color: 'var(--color-text-mute)', fontWeight: 400 }}>No other active Owl accounts yet.</span>
                  : null}
            </label>
            <label style={lab}>Work start
              <input type="time" value={ws} onChange={e => setWs(e.target.value)} aria-label="Work start" style={inp} />
            </label>
            <label style={lab}>Work end
              <input type="time" value={we} onChange={e => setWe(e.target.value)} aria-label="Work end" style={inp} />
            </label>
            <label style={lab}>Work days
              <input value={wd} onChange={e => setWd(e.target.value)} placeholder="Mon-Fri" style={inp} />
            </label>
          </div>

          {picked && (
            <div style={{ marginTop: 10, fontSize: 12.5, padding: '8px 11px', borderRadius: 8,
                          background: alreadyOnTeam ? '#fffbeb' : '#eff6ff',
                          border: `1px solid ${alreadyOnTeam ? '#fde68a' : '#bfdbfe'}`,
                          color: alreadyOnTeam ? '#92400e' : '#1e40af' }}>
              {alreadyOnTeam
                ? <><b>{picked.display_name ?? picked.email}</b> is already on the support team as <b>{TIER_LABEL[picked.support_role!] ?? picked.support_role}</b>. Change their tier from the members list below instead.</>
                : <><b>{picked.display_name ?? picked.email}</b> already has an Owl account — no invitation will be sent. They get the support role and an email telling them so.</>}
              {' '}<button type="button" onClick={() => setPicked(null)} style={linkBtn}>invite a new person instead</button>
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-mute)', marginBottom: 6 }}>
              Customer coverage
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {customers.map(c => (
                <label key={c.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={cov.has(c.id)}
                    onChange={e => setCov(prev => { const n = new Set(prev); e.target.checked ? n.add(c.id) : n.delete(c.id); return n })} />
                  {c.name}
                </label>
              ))}
            </div>
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button disabled={busy || alreadyOnTeam || (!picked && !email.includes('@'))} onClick={submit}
              style={{ border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600,
                       cursor: busy || alreadyOnTeam ? 'not-allowed' : 'pointer', opacity: alreadyOnTeam ? .5 : 1,
                       background: 'var(--color-primary, #0f766e)', color: '#fff' }}>
              {submitLabel}
            </button>
            <span style={{ fontSize: 11.5, color: 'var(--color-text-mute)' }}>
              {picked
                ? 'Profile, hours, manager and coverage are applied immediately.'
                : 'Profile, hours, manager and coverage apply automatically when they accept.'}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

const banner = (bg: string, border: string, fg: string): React.CSSProperties => ({
  display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 12, padding: '10px 12px',
  borderRadius: 8, background: bg, border: `1px solid ${border}`, color: fg, fontSize: 13,
  wordBreak: 'break-word',
})
const dismiss: React.CSSProperties = { border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', padding: 0, lineHeight: 1 }
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--color-text-mute)' }
const inp: React.CSSProperties = { padding: '7px 9px', borderRadius: 7, border: '1px solid var(--color-border)', fontSize: 13, background: 'var(--color-bg)', color: 'var(--color-text)', fontWeight: 400 }
const sugg: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30, marginTop: 2,
  background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8,
  boxShadow: '0 6px 20px rgba(0,0,0,.12)', overflow: 'hidden',
}
const suggRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
  padding: '7px 9px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13,
  color: 'var(--color-text)', fontWeight: 400,
}
const linkBtn: React.CSSProperties = {
  border: 'none', background: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
  color: 'inherit', textDecoration: 'underline',
}
